import React, { useContext, useEffect, useRef, useState } from 'react';
import { ToastContext } from 'app/context/ToastContext';
import { Divider } from 'primereact/divider';
import './contract-page.css';
import axios from 'axios';
import { Button } from 'reactstrap';
import { FileUpload } from 'primereact/fileupload';
import { InputTextarea } from 'primereact/inputtextarea';
import { InputText } from 'primereact/inputtext';
import { useNavigate } from 'react-router-dom';
import { useParams } from 'react-router';
import { formatDateFromInstant } from 'app/shared/util/date-utils';

export const NewRequestPage = () => {
  const { id } = useParams<'id'>();

  const { showSuccess, showError } = useContext(ToastContext);
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [message, setMessage] = useState('');
  const [title, setTitle] = useState('');
  const [files, setFiles] = useState([]);
  const fileUploadRef = useRef(null);
  const navigate = useNavigate();

  useEffect(() => {
    if (id) {
      axios
        .get(`/api/client-request-message/messages/${id}`)
        .then(response => {
          setData(response.data);
        })
        .catch(error => {
          console.error('Error fetching sent requests:', error);
          showError('Chyba', error.response?.data?.detail);
        });
    }
  }, []);

  const downloadDocument = async contractDocument => {
    const response = await axios.get(`/api/client-request-message/download/${contractDocument.id}`, {
      responseType: 'blob', // Important for binary data
    });

    // Create a URL for the blob
    const url = window.URL.createObjectURL(new Blob([response.data]));
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', contractDocument.originalFilename); // Set the original file name
    document.body.appendChild(link);
    link.click();
    link.remove();
  };

  const onDownload = rowData => {
    downloadDocument(rowData);
  };

  const handleSelect = e => {
    // e.files is an array of the newly selected files.
    // If you want to allow multiple selections across multiple picks,
    // you might combine them with existing files in state.
    setFiles(prev => [...prev, ...e.files]);
  };

  function createNewRequest() {
    const formData = new FormData();

    formData.append('message', message);
    formData.append('title', title);

    if (!message) {
      showError('Chyba', 'Správa musí byť vyplnená.');
      return;
    }

    if (!id && !title) {
      showError('Chyba', 'Predmet žiadosti musí byť vyplnený.');
      return;
    }

    // Append each selected file to formData
    files.forEach(file => {
      formData.append('attachments', file);
    });

    axios
      .post('/api/client-request-message/send/1', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      })
      .then(r => {
        setMessage('');
        setTitle('');
        setFiles([]);
        if (fileUploadRef.current) {
          fileUploadRef.current.clear();
        }
        showSuccess('Úspech', 'Správa bola úspešne odoslaná.');
      })
      .catch(e => {
        console.error(e);
        showError('Chyba', e.response?.data?.detail);
      });
  }

  const sendAnswer = () => {
    const formData = new FormData();

    formData.append('message', message);

    // Append each selected file to formData
    files.forEach(file => {
      formData.append('attachments', file);
    });

    axios
      .post(`/api/client-request-message/sendAnswer/${id}`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      })
      .then(r => {
        setMessage('');
        setFiles([]);
        if (fileUploadRef.current) {
          fileUploadRef.current.clear();
        }

        setData(r.data?.messages);
        showSuccess('Úspech', 'Správa bola úspešne odoslaná.');
      })
      .catch(e => {
        console.error(e);
        showError('Chyba', e.response?.data?.detail);
      });
  };

  const handleSubmit = () => {
    if (id) {
      sendAnswer();
    } else {
      createNewRequest();
    }
  };

  return (
    <div>
      <h1 style={{ color: 'var(--primary-color)' }}>NOVÁ ŽIADOSŤ</h1>

      <Divider className="client-divider" />

      <div className="new-request-container" style={{ borderColor: '#b9985a !important' }}>
        <div className="p-3">
          {data?.length > 0 ? (
            data.map((req, index) => (
              <div
                key={index}
                className="border mb-3 p-3"
                style={{
                  borderColor: 'var(--primary-color)',
                  backgroundColor: req.isAdminMessage ? 'rgba(238,189,83,0.8)' : 'rgba(243,232,207,0.8)',
                  justifyContent: 'center',
                  marginLeft: req.isAdminMessage ? '3rem' : '0rem',
                  marginRight: req.isAdminMessage ? '0rem' : '3rem',
                }}
              >
                {/* Display whatever data you like here, e.g. a message: */}
                <div
                  style={{
                    color: '#666',
                  }}
                >
                  <div>
                    <b>{req.isAdminMessage ? 'Odpoveď od NFA' : 'Správa od Vás'}</b>
                  </div>
                  <p>{req.text}</p>
                  {req.documents && req.documents.length > 0 && (
                    <div className="message-attachments">
                      {req.documents.map(attachment => (
                        <a key={attachment.id} onClick={() => onDownload(attachment)} download className="attachment-link">
                          {attachment.originalFilename}
                        </a>
                      ))}
                    </div>
                  )}
                  <div className="message-timestamp">{formatDateFromInstant(req.createdDate)}</div>
                </div>
              </div>
            ))
          ) : (
            <p className="text-muted">Žiadne odoslané alebo prijaté správy.</p>
          )}
        </div>
      </div>

      <div className="border new-request-container" style={{ borderColor: 'var(--primary-color)' }}>
        {/* Box heading in gold background */}
        <div style={{ backgroundColor: 'var(--primary-color)', padding: '0.5rem' }}>
          <h5 className="mb-0 text-white">NOVÁ ŽIADOSŤ</h5>
        </div>

        {/* Text area for the request message */}
        {!id && (
          <div className="p-3">
            <label htmlFor="title">Predmet*</label>
            <InputText
              id="title"
              style={{ width: '100%' }}
              placeholder="Sem napíšte predmet k Vašej žiadosti.."
              value={title}
              onChange={e => setTitle(e.target.value)}
            />
          </div>
        )}
        <div className="p-3">
          <label htmlFor="message">Správa*</label>
          <InputTextarea
            id="message"
            rows={5}
            autoResize
            style={{ width: '100%' }}
            placeholder="Sem napíšte sprievodný text k Vašej žiadosti."
            value={message}
            onChange={e => setMessage(e.target.value)}
          />
        </div>

        {/* File uploader & Submit buttons */}
        <div className="px-3 pb-3">
          {/* Multiple file upload with "Pridať prílohu" */}
          <FileUpload
            ref={fileUploadRef}
            name="attachments"
            multiple
            accept=".csv,.xml,.pdf,.xlsx,.png,.jpg,.jpeg,.doc,.docx,.xls,.ppt,.pptx,.txt,.odt,.ods,.odp,.ics"
            mode="advanced"
            customUpload
            auto={false} // don't auto-upload
            chooseLabel="Pridať prílohu"
            cancelLabel="Zrušiť"
            uploadLabel="Nahrať"
            onSelect={handleSelect}
            chooseOptions={{ icon: 'pi pi-paperclip' }}
            style={{ backgroundColor: 'white', background: 'white' }}
          />

          {/* Submit button */}
          <div className="flex mt-2" style={{ justifyContent: 'end' }}>
            <Button
              className="p-button ms-2"
              style={{ paddingLeft: '2rem', paddingRight: '2rem' }}
              icon="pi pi-check"
              onClick={() => navigate('/requests')}
            >
              Späť
            </Button>
            <Button
              className="p-button ms-2"
              style={{ paddingLeft: '2rem', paddingRight: '2rem', backgroundColor: 'var(--primary-color)' }}
              icon="pi pi-check"
              onClick={handleSubmit}
            >
              Odoslať
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default NewRequestPage;
